import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";

import { catchError, finalize, tap } from "rxjs";

import { SubPageComponent } from "@shared/components/sub-page/sub-page.component";
import { ButtonComponent } from "@shared/components/button/button.component";
import { getContractualMonth } from "@shared/functions/date.functions";
import { convertToHHMM, formatTimeInput } from "@shared/functions";
import { GlobalService, PlanApiService } from "@shared/services";
import { PlanRecord, PlanUpdateRecord } from "@shared/models";
import { MessageService } from "primeng/api";

@Component({
  selector: "app-plan-month",
  standalone: true,
  imports: [SubPageComponent, ButtonComponent, FormsModule, ReactiveFormsModule],
  templateUrl: "./plan-month.component.html",
  styleUrl: "./plan-month.component.scss",
})
export class PlanMonthComponent implements OnInit {
  activatedRoute = inject(ActivatedRoute);
  globalService = inject(GlobalService);
  planApiService = inject(PlanApiService);
  messageService = inject(MessageService);
  pageTitle: string = "";
  planKey: number = 0;
  effectivePeriod: number = 0;
  unsavedChanges: boolean = false;
  monthData: PlanUpdateRecord = {} as PlanUpdateRecord;
  initialFormValues: any = {};
  requiredCredit: string = "";
  isLoading: boolean = false;
  navBackRoute: string = "";

  monthDataInputs = {
    timeInputs: [
      { label: "VMAX", formControlName: "vmax" },
      { label: "PROJ", formControlName: "proj" },
      { label: "IMAX", formControlName: "imax" },
    ],
    dayInputs: [
      { label: "PVDs", formControlName: "plannedPVDs" },
      { label: "Sick", formControlName: "plannedSick" },
      { label: "Training", formControlName: "plannedTraining" },
    ],
  };

  monthDataForm = new FormGroup({
    // vmax: new FormControl(""),
    // proj: new FormControl(""),
    // imax: new FormControl(""),
    plannedPVDs: new FormControl(0),
    plannedSick: new FormControl(0),
    plannedTraining: new FormControl(0),
  });

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      this.planKey = params["planKey"];
      this.effectivePeriod = params["effectivePeriod"];
      this.navBackRoute = this.planKey == 0 ? "/active" : "/standby";
    });
    this.setPageTitle();
    this.getMonthData();
  }

  getMonthData(): void {
    this.planApiService
      .getPlan(false, Number(this.planKey))
      .pipe(
        tap((response) => {
          if (response.response) {
            const monthRecordData = response.response.plans.find(
              (p) => p.effectivePeriod === Number(this.effectivePeriod)
            );
            if (monthRecordData) {
              this.monthData = {
                key: this.planKey,
                effectivePeriod: this.effectivePeriod,
                vmax: monthRecordData.vmax,
                proj: monthRecordData.proj,
                imax: monthRecordData.imax,
                plannedPVDs: monthRecordData.plannedPVDs,
                plannedSick: monthRecordData.plannedSick,
                plannedTraining: monthRecordData.plannedTraining,
              };
              this.monthDataForm.setValue({
                plannedPVDs: monthRecordData.plannedPVDs,
                plannedSick: monthRecordData.plannedSick,
                plannedTraining: monthRecordData.plannedTraining,
              });
              this.calculateRequiredCredit(this.monthDataForm.value);
              this.initialFormValues = this.monthDataForm.value;
            }
          }
        })
      )
      .subscribe();
  }

  calculateRequiredCredit(monthRecord: any): void {
    const DAY_VALUE: number = 315;
    const pvdsValue = monthRecord.plannedPVDs * DAY_VALUE;
    const sickValue = monthRecord.plannedSick * DAY_VALUE;
    const trainingValue = monthRecord.plannedTraining * DAY_VALUE;
    const projValue = this.monthData.proj || 0;
    const requiredCredit = projValue - (pvdsValue + sickValue + trainingValue);
    this.requiredCredit = convertToHHMM({ minutes: requiredCredit });
  }

  setPageTitle(): void {
    const year = this.effectivePeriod.toString().substring(0, 4);
    const month = getContractualMonth({ month: this.effectivePeriod % 100 }).monthLong;
    this.pageTitle = `${month} ${year}`;
  }

  onKeyDown(keystroke: KeyboardEvent, formControlName: string) {
    const newValue = formatTimeInput(keystroke);
    this.monthDataForm.get(formControlName)?.setValue(newValue);
    this.unsavedChanges = this.checkForChanges();
    this.calculateRequiredCredit(this.monthDataForm.value);
  }

  onKeyUp(): void {
    const { plannedPVDs, plannedSick, plannedTraining } = this.monthDataForm.value;
    this.monthDataForm.patchValue({
      plannedPVDs: plannedPVDs ?? 0,
      plannedSick: plannedSick ?? 0,
      plannedTraining: plannedTraining ?? 0,
    });
    this.calculateRequiredCredit(this.monthDataForm.value);
    this.unsavedChanges = this.checkForChanges();
  }

  checkForChanges(): boolean {
    const currentValues = this.monthDataForm.value;
    return Object.keys(this.initialFormValues).some(
      (key) =>
        this.initialFormValues[key as keyof typeof currentValues] !== currentValues[key as keyof typeof currentValues]
    );
  }

  convertDataValue(value: number | undefined): string {
    return convertToHHMM({ minutes: value || 0 });
  }

  save(): void {
    const formValue = this.monthDataForm.value;
    if (formValue.plannedPVDs != null && formValue.plannedSick != null && formValue.plannedTraining != null) {
      const updatedData: PlanUpdateRecord = {
        key: this.planKey,
        effectivePeriod: this.effectivePeriod,
        plannedPVDs: formValue.plannedPVDs,
        plannedSick: formValue.plannedSick,
        plannedTraining: formValue.plannedTraining,
      };
      this.isLoading = true;
      this.globalService.saving.set(true);
      this.planApiService
        .updatePlanRecords([updatedData])
        .pipe(
          tap((response) => {
            if (response && response.length > 0) {
              this.messageService.clear();
              this.messageService.add({
                severity: "success",
                summary: "Saved",
                detail: "Record updated successfully.",
                key: "noReply",
              });
              this.unsavedChanges = false;
              this.initialFormValues = this.monthDataForm.value;
            }
          }),
          finalize(() => {
            this.isLoading = false;
            this.globalService.saving.set(false);
          })
        )
        .subscribe();
    } else {
      console.error("Form contains invalid values.");
    }
  }
}
